<template>
  <div class="context-selector">
    <component
      :is="switchable ? 'button' : 'div'"
      :type="switchable ? 'button' : null"
      class="context-selector__trigger"
      :class="{ 'context-selector__trigger--static': !switchable }"
      :aria-haspopup="switchable ? 'true' : null"
      @click="onTrigger"
    >
      <span class="context-glyph context-glyph--active">
        <i :class="['pi', `pi-${context.icon}`]" aria-hidden="true" />
      </span>
      <span class="context-selector__meta">
        <span class="context-selector__name">{{ context.name }}</span>
        <span v-if="context.description" class="context-selector__desc">{{ context.description }}</span>
      </span>
      <i
        v-if="switchable"
        :class="['pi', open ? 'pi-angle-up' : 'pi-angle-down', 'context-selector__caret']"
        aria-hidden="true"
      />
    </component>

    <Popover v-if="switchable" ref="popoverRef" append-to="body" @hide="open = false" @show="open = true">
      <div class="context-switch">
        <button
          v-for="row in rows"
          :key="row.id"
          type="button"
          class="context-switch__row"
          :class="{ 'context-switch__row--active': row.active, 'context-switch__row--disabled': row.disabled }"
          :disabled="row.disabled || row.active"
          @click="onSelect(row)"
        >
          <span class="context-glyph" :class="{ 'context-glyph--active': row.active }">
            <i :class="['pi', `pi-${row.icon}`]" aria-hidden="true" />
          </span>
          <span class="context-switch__meta">
            <span class="context-switch__name">{{ row.name }}</span>
            <span v-if="row.description" class="context-switch__desc">{{ row.description }}</span>
          </span>
          <i v-if="row.active" class="pi pi-check context-switch__check" aria-hidden="true" />
          <Tag v-else-if="row.disabled" value="Soon" severity="secondary" class="context-switch__soon" />
        </button>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import Popover from 'primevue/popover'
import Tag from 'primevue/tag'

// The sidebar's context switcher, and the masthead when there is nothing to
// switch to. A CONTEXT is the level above Sections and Pages — Platform
// Administration, Auction Operations — which the design calls an "App". That
// word collides with the webapp itself, so here it is a context (ruled
// 2026-09-22). `context` is the current nav; `contexts` is the full list. The
// active one is matched by id; a disabled one is inert with a "Soon" tag.
//
// With one context or none the trigger loses its caret, its popover and its
// button element, and stands as a plain identity block: an affordance that
// answers nothing is worse than no affordance.
defineOptions({ name: 'ContextSelector' })

const props = defineProps({
  // `{ id, name, description?, icon }` — the nav tree of the current context.
  context: { type: Object, required: true },
  // Every context the host offers, the current one included.
  contexts: { type: Array, default: () => [] },
})
const emit = defineEmits(['select-context'])

const popoverRef = ref(null)
const open = ref(false)

// Active context first, then the rest in given order.
const rows = computed(() => {
  const list = props.contexts.map((each) => ({ ...each, active: each.id === props.context.id }))
  return [...list.filter((each) => each.active), ...list.filter((each) => !each.active)]
})

// There has to be somewhere else to go. A list holding only the context already
// on screen is a switcher with one row, and that row is disabled.
const switchable = computed(() => rows.value.some((each) => !each.active))

function onTrigger(event) {
  if (!switchable.value) return
  popoverRef.value?.toggle(event)
}

function onSelect(row) {
  popoverRef.value?.hide()
  if (!row.active && !row.disabled) emit('select-context', row.id)
}
</script>

<style scoped>
.context-selector {
  position: relative;
}

.context-selector__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.context-glyph {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--p-overlay-popover-background);
  color: var(--p-text-muted-color);
  font-size: 14px;
}

.context-glyph--active {
  background: linear-gradient(135deg, var(--p-primary-600), var(--p-primary-400));
  color: var(--p-primary-950);
}

.context-selector__meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.context-selector__name {
  font-size: 13px;
  font-weight: 800;
  color: var(--p-text-color);
  white-space: nowrap;
}

.context-selector__desc {
  font-size: 10px;
  color: var(--p-surface-400);
  margin-top: 1px;
}

.context-selector__caret {
  font-size: 10px;
  color: var(--p-surface-400);
  margin-left: auto;
  flex-shrink: 0;
}

.context-selector__trigger--static {
  cursor: default;
}

.context-switch {
  width: 224px;
  max-height: min(60vh, 420px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.context-switch__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.context-switch__row--active {
  background: var(--p-overlay-popover-background);
  cursor: default;
}

.context-switch__row--disabled {
  cursor: default;
  opacity: 0.6;
}

.context-switch__row:not(.context-switch__row--active):not(.context-switch__row--disabled):hover {
  background: var(--p-overlay-popover-background);
}

.context-switch__meta {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.context-switch__name {
  font-size: 13px;
  font-weight: 700;
  color: var(--p-text-color);
  white-space: nowrap;
}

.context-switch__desc {
  font-size: 10px;
  color: var(--p-surface-400);
  margin-top: 1px;
}

.context-switch__check {
  font-size: 10px;
  color: var(--p-primary-500);
  flex-shrink: 0;
}

.context-switch__soon :deep(.p-tag) {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
</style>
